import { forwardRef, ForwardRefRenderFunction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

// #region redux
import { AppState } from "store";
// #endregion

// #region types
import { WintSalaryEnums } from "models/enums";
import { SalaryFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { EarlyPayoutFormContainerProps } from "models/propTypes/wintSalaryPropTypes";
import { WintSuperkollAPIModelWintSalaryEarlyPayout } from "wint-api-client/dist/types";
// #endregion

// #region components
import useDeviationPermissions from "components/salaryDeviation/usePermissions";
import useEarlyPayoutService from "serviceHooks/useEarlyPayoutService";
import useWintSalaryService from "serviceHooks/useWintSalaryService";
import { TranslationKeys } from "translations";
import { DateHelper } from "utils/helpers";
import EarlyPayoutForm from "./earlyPayoutForm";
// #endregion

const isCancelledRequest = (error: any) => error?.status === "cancelled";

const EarlyPayoutFormContainer: ForwardRefRenderFunction<FormHandles, EarlyPayoutFormContainerProps> = (props, ref) => {
  const { onFormValidationChange, preSelectedEmployee } = props;
  const loggedInUser = useSelector((state: AppState) => state.auth.loggedInUser);
  const { submitEarlyPayout, getEarlyPayoutInfo, previewEarlyPayout } = useEarlyPayoutService();
  const { searchPersonsAutoComplete } = useWintSalaryService();
  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();
  const [loggedInUserEmployee, setLoggedInUserEmployee] = useState<AutocompleteListItem | undefined>(undefined);
  const defaultEmployee = preSelectedEmployee ?? loggedInUserEmployee;
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const formValidityRef = useRef(false);
  const infoAbortRef = useRef<AbortController | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);
  const [preview, setPreview] = useState<WintSuperkollAPIModelWintSalaryEarlyPayout.EarlyPayoutPreview | undefined>(
    undefined
  );
  const [earlyPayoutInfo, setEarlyPayoutInfo] = useState<
    WintSuperkollAPIModelWintSalaryEarlyPayout.EarlyPayoutInitialInfo | undefined
  >(undefined);

  const validPayoutDays = earlyPayoutInfo?.validPayoutDays?.map((d) => new Date(d));
  const earliestPayoutDate = validPayoutDays?.length
    ? new Date(Math.min(...validPayoutDays.map((d) => d.getTime())))
    : undefined;
  const latestPayoutDate = validPayoutDays?.length
    ? new Date(Math.max(...validPayoutDays.map((d) => d.getTime())))
    : undefined;

  // Block submit when the preview flags insufficient net (hasSpace=false). Matches the
  // Förskott → Extra lön auto-switch: the form must not let the user approve a payout that
  // would drive remaining net negative, even when already on Extra lön.
  const hasPreviewSpace = preview === undefined || preview.hasSpace;

  const handleFormValidationChange = useCallback(
    (isValid: boolean) => {
      formValidityRef.current = isValid;
      onFormValidationChange?.(!loading && !previewLoading && isValid && hasPreviewSpace);
    },
    [loading, previewLoading, onFormValidationChange, hasPreviewSpace]
  );

  useEffect(() => {
    onFormValidationChange?.(!loading && !previewLoading && formValidityRef.current && hasPreviewSpace);
  }, [loading, previewLoading, onFormValidationChange, hasPreviewSpace]);

  const validationSchema = useMemo(() => {
    let payoutDateSchema = Yup.date()
      .typeError(TranslationKeys.Validations.InvalidDate)
      .required(TranslationKeys.Validations.Required);

    if (earliestPayoutDate) {
      payoutDateSchema = payoutDateSchema.min(earliestPayoutDate, TranslationKeys.Validations.InvalidDate);
    }
    if (latestPayoutDate) {
      payoutDateSchema = payoutDateSchema.max(latestPayoutDate, TranslationKeys.Validations.InvalidDate);
    }
    if (validPayoutDays?.length) {
      payoutDateSchema = payoutDateSchema.test("valid-payout-day", TranslationKeys.Validations.InvalidDate, (value) => {
        if (!value) return true;
        return validPayoutDays.some(
          (d) =>
            d.getFullYear() === value.getFullYear() &&
            d.getMonth() === value.getMonth() &&
            d.getDate() === value.getDate()
        );
      });
    }

    return Yup.object<SalaryFormTypes.EarlyPayout>({
      employee: Yup.object<AutocompleteListItem>().required(TranslationKeys.Validations.Required),
      payoutDate: payoutDateSchema,
      amountType: Yup.number().required(TranslationKeys.Validations.Required),
      payoutAmount: Yup.number()
        .required(TranslationKeys.Validations.Required)
        .min(1, TranslationKeys.Salary.ExtraPayoutMinimumPayoutAmount),
      payoutType: Yup.number().required(TranslationKeys.Validations.Required),
    });
  }, [earliestPayoutDate, latestPayoutDate, validPayoutDays, earlyPayoutInfo]);

  useEffect(() => {
    if (preSelectedEmployee || !loggedInUser.personId) return;

    // Guard against querying the full employee list with an empty search — searchPersonsAutoComplete
    // treats an empty string as "no filter" and can return a very large result set.
    const searchName = loggedInUser.name?.trim();
    if (!searchName) return;

    let cancelled = false;
    (async () => {
      try {
        const results = await searchPersonsAutoComplete(
          searchName,
          false,
          true,
          isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
        );
        if (cancelled) return;
        if (results.some((r) => Number(r.id) === loggedInUser.personId)) {
          setLoggedInUserEmployee({
            id: loggedInUser.personId,
            value: loggedInUser.name,
            title: loggedInUser.name,
          });
        }
      } catch {
        // leave undefined
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    preSelectedEmployee,
    loggedInUser.personId,
    loggedInUser.name,
    isBusinessUnitManagerWithSalaryRights,
    isSalaryAdmin,
    isDeviationAdmin,
  ]);

  const initialValues: SalaryFormTypes.EarlyPayout = {
    amountType: WintSalaryEnums.EarlyPayoutAmountType.Net,
    payoutType: WintSalaryEnums.EarlyPayoutKind.ExtraSalary,
    ...(defaultEmployee ? { employee: defaultEmployee } : {}),
  };

  const loadEarlyPayoutInfo = useCallback(
    async (employeeId: number, yearMonth?: number) => {
      infoAbortRef.current?.abort();
      const controller = new AbortController();
      infoAbortRef.current = controller;

      setLoading(true);
      try {
        const info = await getEarlyPayoutInfo(employeeId, yearMonth, { signal: controller.signal });
        setEarlyPayoutInfo(info);
      } catch (error) {
        if (isCancelledRequest(error)) return;
        // A month without a salary must not take the date picker down with it: the valid days do not
        // depend on the month, so keep them. Recording the requested month stops the form from asking
        // for the same failing month again.
        setEarlyPayoutInfo((previous) =>
          previous && yearMonth !== undefined ? { ...previous, yearMonth, payoutInfo: undefined } : undefined
        );
      } finally {
        if (infoAbortRef.current === controller) {
          setLoading(false);
        }
      }
    },
    [getEarlyPayoutInfo]
  );

  const handleEmployeeChange = useCallback(
    async (employeeId: number) => {
      previewAbortRef.current?.abort();
      setPreview(undefined);
      setEarlyPayoutInfo(undefined);
      await loadEarlyPayoutInfo(employeeId);
    },
    [loadEarlyPayoutInfo]
  );

  const handlePreview = async (values: SalaryFormTypes.EarlyPayout) => {
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;

    setPreviewLoading(true);
    try {
      const yearMonth = DateHelper.getYearMonth(values.payoutDate!);
      const result = await previewEarlyPayout(values, yearMonth, { signal: controller.signal });
      setPreview(result);
    } catch (error) {
      if (isCancelledRequest(error)) return;
      setPreview(undefined);
    } finally {
      if (previewAbortRef.current === controller) {
        setPreviewLoading(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      infoAbortRef.current?.abort();
      previewAbortRef.current?.abort();
    };
  }, []);

  const submitHandler = async (values: SalaryFormTypes.EarlyPayout) => {
    const yearMonth = DateHelper.getYearMonth(values.payoutDate!);
    const res = await submitEarlyPayout(values, yearMonth);
    return res;
  };

  return (
    <EarlyPayoutForm
      ref={ref}
      defaultValues={initialValues}
      validationSchema={validationSchema}
      handleSubmit={submitHandler}
      onFormValidationChange={handleFormValidationChange}
      preview={preview}
      onEmployeeChange={handleEmployeeChange}
      onPayoutMonthChange={loadEarlyPayoutInfo}
      validPayoutDays={validPayoutDays}
      earlyPayoutInfo={earlyPayoutInfo}
      onPreview={handlePreview}
      loading={loading}
      previewLoading={previewLoading}
    />
  );
};

export default forwardRef(EarlyPayoutFormContainer);
